import React, { useState } from 'react';
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Button, Divider, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowRight from '@mui/icons-material/ArrowRight';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ChatIcon from '@mui/icons-material/Chat';
import DynamicInfographic from '../../components/infographics/DynamicInfographic';
import FallRiskInfographics from '../../components/infographics/FallRiskInfographics';
import ModuleSidebar from '../../components/navigation/ModuleSidebar';
import { scrollToTop } from '../../utils/scrollUtils';

const CaregiverBestPractices = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('3.1');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [answerStatus, setAnswerStatus] = useState({});
  
  const handleBack = () => {
    navigate('/courses/fall-prevention');
    scrollToTop();
  };

  // Function to navigate to the next section
  const handleNextSection = () => {
    const currentSectionIndex = sections.findIndex(section => section.id === activeSection);
    if (currentSectionIndex < sections.length - 1) {
      const nextSection = sections[currentSectionIndex + 1].id;
      setActiveSection(nextSection);
      scrollToTop();
    }
  };

  // Define correct answers for each question
  const correctAnswers = {
    '3.1-1': 1, // Lock wheels on wheelchairs, beds, and commodes
    '3.1-2': 0, // Position wheelchair at 45° angle to bed
    '3.1-3': 2, // Bend knees, keep back straight during transfer
    '3.2-1': 0, // Allow extra time for completion without rushing
    '3.2-2': 2, // Break complex activities into manageable steps
    '3.2-3': 1, // Consider physical, cognitive, and emotional factors
    '3.3-1': 1, // Use leg muscles instead of back muscles
    '3.3-2': 2, // Maintain proximity to the person or object
    '3.3-3': 0, // Perform regular stretching exercises
    '3.4-1': 2, // Acknowledge fears while building confidence
    '3.4-2': 1, // Visual examples improve understanding and confidence
    '3.4-3': 0, // Creates connection and shows attentiveness
  };

  const handleAnswerSelect = (sectionId, questionId, answerIndex) => {
    const key = `${sectionId}-${questionId}`;
    
    // Check if the correct answer has already been selected for this question
    // If so, don't allow further selections
    if (answerStatus[key] === true) {
      return;
    }
    
    // Store the selected answer
    setSelectedAnswers(prev => ({
      ...prev,
      [key]: answerIndex
    }));
    
    // Check if the selected answer is correct
    const isCorrect = answerIndex === correctAnswers[key];
    
    // Only show explanation if the correct answer is selected
    if (isCorrect) {
      setShowExplanations(prev => ({
        ...prev,
        [key]: true
      }));
    } else {
      // If incorrect, ensure explanation is hidden
      setShowExplanations(prev => ({
        ...prev,
        [key]: false
      }));
    }
    
    // Track if answer is correct or incorrect
    setAnswerStatus(prev => ({
      ...prev,
      [key]: isCorrect
    }));
  };

  // Function to handle section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    scrollToTop();
  };
  
  // Define all sections for the module
  const sections = [
    {
      id: '3.1',
      title: '3.1 Safe Techniques for Transfers and Ambulation',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <TransferWithinAStationIcon sx={{ mr: 1, color: '#81c784' }} /> 3.1 Safe Techniques for Transfers and Ambulation
          </Typography>
          
          <Typography paragraph>
            Proper transfer and ambulation techniques are essential for preventing falls and injuries for both 
            the care recipient and the caregiver. These techniques should be practiced regularly to ensure safety.
          </Typography>
          
          {/* Dynamic Infographic - Switch between pre-designed and AI-generated */}
          <DynamicInfographic module="CaregiverBestPractices" topic="Safe Transfer and Mobility Techniques" />

          <Box sx={{ bgcolor: 'rgba(129, 199, 132, 0.1)', p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Key Transfer Principles</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#81c784', fontWeight: 600 }}>
                      Preparation
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                        <ListItemText primary="Clear the path of obstacles" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                        <ListItemText primary="Lock wheels on wheelchairs, beds, and commodes" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                        <ListItemText primary="Explain the process to the care recipient" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#81c784', fontWeight: 600 }}>
                      Execution
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                        <ListItemText primary="Use a transfer belt when appropriate" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                        <ListItemText primary="Maintain proper body alignment" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                        <ListItemText primary="Move smoothly and avoid sudden movements" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#81c784', fontWeight: 600 }}>
                      Follow-through
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                        <ListItemText primary="Ensure stability before releasing support" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                        <ListItemText primary="Position assistive devices within reach" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                        <ListItemText primary="Check comfort and alignment" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Common Transfer Scenarios</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Bed to Wheelchair Transfer</Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><AccessibilityNewIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Position wheelchair at 45° angle to bed" 
                        secondary="Lock wheels and remove or swing away footrests"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><AccessibilityNewIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Help person to sitting position on edge of bed" 
                        secondary="Allow time for balance and to prevent dizziness"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><AccessibilityNewIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Apply transfer belt if needed" 
                        secondary="Grasp belt at sides or back, never front"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><AccessibilityNewIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Pivot person to wheelchair" 
                        secondary="Bend knees, keep back straight during transfer"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Sit to Stand Assistance</Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><AccessibilityNewIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Position person at edge of seat" 
                        secondary="Feet flat on floor, slightly apart and back"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><AccessibilityNewIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Place hands on transfer belt or waist" 
                        secondary="Stand directly in front or slightly to side"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><AccessibilityNewIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Rock forward on count of three" 
                        secondary="Use momentum to assist standing"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><AccessibilityNewIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Stabilize person before walking" 
                        secondary="Position walker or cane if needed"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 3.1 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#81c784', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of the following is the MOST important preparation step before transferring a patient?
                    </Typography>
                    <List dense>
                      {['Clear the path of obstacles', 'Lock wheels on wheelchairs, beds, and commodes', 'Explain the process to the care recipient'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['3.1-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['3.1-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['3.1-1'] && index === correctAnswers['3.1-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('3.1', '1', index)}
                            disabled={answerStatus['3.1-1'] === true && index !== correctAnswers['3.1-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['3.1-1'] === true && index !== correctAnswers['3.1-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['3.1-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(129, 199, 132, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Locking wheels on wheelchairs, beds, and commodes is the most important safety measure before a transfer to prevent movement during the transfer process, which could lead to falls and injuries.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the first step in a bed to wheelchair transfer?
                    </Typography>
                    <List dense>
                      {['Position wheelchair at 45° angle to bed', 'Apply transfer belt', 'Help person to sitting position on edge of bed'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['3.1-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['3.1-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['3.1-2'] && index === correctAnswers['3.1-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('3.1', '2', index)}
                            disabled={answerStatus['3.1-2'] === true && index !== correctAnswers['3.1-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['3.1-2'] === true && index !== correctAnswers['3.1-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['3.1-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(129, 199, 132, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          The first step in a bed to wheelchair transfer is to position the wheelchair at a 45° angle to the bed, which creates the shortest pivot distance and minimizes the risk of falls during the transfer.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      When pivoting a person to a wheelchair, what is the proper body mechanics technique for the caregiver?
                    </Typography>
                    <List dense>
                      {['Keep back straight and lift with arms', 'Keep feet together for stability', 'Bend knees, keep back straight during transfer'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['3.1-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['3.1-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['3.1-3'] && index === correctAnswers['3.1-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('3.1', '3', index)}
                            disabled={answerStatus['3.1-3'] === true && index !== correctAnswers['3.1-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['3.1-3'] === true && index !== correctAnswers['3.1-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['3.1-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(129, 199, 132, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          The proper body mechanics technique is to bend your knees and keep your back straight during the transfer. This uses your stronger leg muscles rather than your back muscles, reducing the risk of caregiver injury.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '3.2',
      title: '3.2 Supporting Independence While Ensuring Safety',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <AccessibilityNewIcon sx={{ mr: 1, color: '#81c784' }} /> 3.2 Supporting Independence While Ensuring Safety
          </Typography>
          
          <Typography paragraph>
            Finding the balance between promoting independence and ensuring safety is a key challenge for caregivers. 
            This section provides evidence-based strategies for supporting autonomy while minimizing fall risks across different care settings and functional levels.
          </Typography>
          
          <Box sx={{ mb: 3, p: 3, borderRadius: 2, bgcolor: 'rgba(129, 199, 132, 0.1)', border: '1px solid rgba(129, 199, 132, 0.3)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2e7d32', fontWeight: 600, borderBottom: '1px solid rgba(129, 199, 132, 0.5)', pb: 1 }}>
              The Autonomy-Safety Balance: Core Principles
            </Typography>
            
            <Typography paragraph sx={{ mt: 2 }}>
              Research shows that excessive safety measures can lead to decreased mobility, reduced confidence, and accelerated functional decline. 
              Conversely, insufficient safety precautions increase fall risk. The optimal approach involves a personalized balance that adapts to changing needs.
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box sx={{ 
                    minWidth: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    bgcolor: 'rgba(129, 199, 132, 0.2)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>1</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 0.5 }}>
                      Person-Centered Assessment
                    </Typography>
                    <Typography variant="body2">
                      Evaluate cognitive status, physical abilities, personal preferences, and risk tolerance before determining the appropriate level of support and safety measures.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box sx={{ 
                    minWidth: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    bgcolor: 'rgba(129, 199, 132, 0.2)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>2</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 0.5 }}>
                      Graduated Risk Approach
                    </Typography>
                    <Typography variant="body2">
                      Implement the least restrictive interventions first, monitoring outcomes and adjusting the level of support as needed based on performance and changing conditions.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box sx={{ 
                    minWidth: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    bgcolor: 'rgba(129, 199, 132, 0.2)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>3</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 0.5 }}>
                      Shared Decision-Making
                    </Typography>
                    <Typography variant="body2">
                      Involve care recipients in decisions about their safety and independence, respecting their right to make choices even when some risk is involved (dignity of risk).
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box sx={{ 
                    minWidth: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    bgcolor: 'rgba(129, 199, 132, 0.2)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>4</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 0.5 }}>
                      Regular Reassessment
                    </Typography>
                    <Typography variant="body2">
                      Schedule formal reviews of the autonomy-safety balance as abilities change, particularly after health events, medication changes, or observed functional decline.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#81c784', fontWeight: 600 }}>
                    Promoting Independence
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Encourage self-care activities" 
                        secondary="Allow extra time for completion without rushing"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Provide appropriate assistive devices" 
                        secondary="Ensure proper fit and training for optimal use"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Modify tasks to match abilities" 
                        secondary="Break complex activities into manageable steps"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Recognize and praise efforts" 
                        secondary="Positive reinforcement builds confidence"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Involve in decision-making" 
                        secondary="Include care recipients in planning their care"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#81c784', fontWeight: 600 }}>
                    Safety Considerations
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Assess readiness for independent activities" 
                        secondary="Consider physical, cognitive, and emotional factors"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Create a safe environment" 
                        secondary="Remove hazards and optimize lighting and accessibility"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Establish clear safety protocols" 
                        secondary="Develop and practice emergency response plans"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Use supervision appropriately" 
                        secondary="Adjust level of oversight based on changing needs"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Recognize signs of fatigue" 
                        secondary="Intervene before exhaustion increases fall risk"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 3.2 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#81c784', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is a key strategy for promoting independence in care recipients?
                    </Typography>
                    <List dense>
                      {['Allow extra time for completion without rushing', 'Complete tasks for them to ensure safety', 'Limit activities to prevent falls'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['3.2-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['3.2-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['3.2-1'] && index === correctAnswers['3.2-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('3.2', '1', index)}
                            disabled={answerStatus['3.2-1'] === true && index !== correctAnswers['3.2-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['3.2-1'] === true && index !== correctAnswers['3.2-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['3.2-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(129, 199, 132, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Allowing extra time for care recipients to complete tasks on their own promotes independence while respecting their dignity. Rushing can lead to anxiety and increased fall risk, while giving sufficient time builds confidence and maintains abilities.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which approach best supports both independence and safety when helping with complex tasks?
                    </Typography>
                    <List dense>
                      {['Complete difficult parts of tasks for the care recipient', 'Encourage them to attempt the entire task unassisted', 'Break complex activities into manageable steps'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['3.2-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['3.2-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['3.2-2'] && index === correctAnswers['3.2-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('3.2', '2', index)}
                            disabled={answerStatus['3.2-2'] === true && index !== correctAnswers['3.2-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['3.2-2'] === true && index !== correctAnswers['3.2-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['3.2-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(129, 199, 132, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Breaking complex activities into manageable steps allows care recipients to maintain independence by completing parts they are capable of while ensuring safety. This approach prevents overwhelming them while preserving dignity and function.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      When assessing readiness for independent activities, what factors should be considered?
                    </Typography>
                    <List dense>
                      {['Only physical abilities', 'Consider physical, cognitive, and emotional factors', 'Just the care recipient\'s stated preferences'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['3.2-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['3.2-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['3.2-3'] && index === correctAnswers['3.2-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('3.2', '3', index)}
                            disabled={answerStatus['3.2-3'] === true && index !== correctAnswers['3.2-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['3.2-3'] === true && index !== correctAnswers['3.2-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['3.2-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(129, 199, 132, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          A comprehensive assessment should consider physical abilities (strength, balance), cognitive factors (judgment, memory), and emotional aspects (confidence, fear of falling). All three domains affect a person's ability to perform activities safely.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '3.3',
      title: '3.3 Proper Body Mechanics for Caregivers',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <FitnessCenterIcon sx={{ mr: 1, color: '#81c784' }} /> 3.3 Proper Body Mechanics for Caregivers
          </Typography>
          
          <Typography paragraph>
            Using proper body mechanics is essential for caregiver safety and longevity. These techniques help prevent 
            injury while providing effective care and assistance to those at risk of falls.
          </Typography>
          
          <Box sx={{ bgcolor: 'rgba(129, 199, 132, 0.1)', p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Core Principles of Body Mechanics</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Box sx={{ bgcolor: '#81c784', color: 'white', width: 60, height: 60, borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto', mb: 2 }}>
                    <Typography variant="h5">1</Typography>
                  </Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Wide Base of Support</Typography>
                  <Typography variant="body2">
                    Keep feet shoulder-width apart to create a stable foundation for movement and lifting.
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Box sx={{ bgcolor: '#81c784', color: 'white', width: 60, height: 60, borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto', mb: 2 }}>
                    <Typography variant="h5">2</Typography>
                  </Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Bend at Knees, Not Waist</Typography>
                  <Typography variant="body2">
                    Use leg muscles instead of back muscles when lifting to prevent strain and injury.
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Box sx={{ bgcolor: '#81c784', color: 'white', width: 60, height: 60, borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto', mb: 2 }}>
                    <Typography variant="h5">3</Typography>
                  </Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Keep Load Close to Body</Typography>
                  <Typography variant="body2">
                    Maintain proximity to the person or object being moved to reduce strain on back and arms.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Typography variant="h6" gutterBottom>Caregiver Self-Care Techniques</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Preventive Measures</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText primary="Perform regular stretching exercises" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText primary="Strengthen core and back muscles" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText primary="Take regular breaks during caregiving shifts" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText primary="Use assistive devices when available" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText primary="Request help for difficult transfers" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Recovery Techniques</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText primary="Apply ice to sore muscles after strenuous activity" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText primary="Practice gentle yoga or stretching at end of day" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText primary="Use proper posture when resting and sleeping" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText primary="Consider massage therapy for chronic tension" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ArrowRight sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText primary="Report persistent pain to healthcare provider" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 3.3 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#81c784', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which of the following is a core principle of proper body mechanics?
                    </Typography>
                    <List dense>
                      {['Keep feet close together for better control', 'Use leg muscles instead of back muscles when lifting', 'Lift with quick, jerking motions for momentum'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['3.3-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['3.3-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['3.3-1'] && index === correctAnswers['3.3-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('3.3', '1', index)}
                            disabled={answerStatus['3.3-1'] === true && index !== correctAnswers['3.3-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['3.3-1'] === true && index !== correctAnswers['3.3-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['3.3-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(129, 199, 132, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Using leg muscles instead of back muscles when lifting is a fundamental principle of proper body mechanics. The legs contain larger, stronger muscle groups that can handle weight more effectively than the more vulnerable back muscles.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is it important to keep a wide base of support during caregiving activities?
                    </Typography>
                    <List dense>
                      {['It allows you to move more quickly', 'It helps you reach farther when assisting patients', 'It creates a stable foundation for movement and lifting'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['3.3-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['3.3-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['3.3-2'] && index === correctAnswers['3.3-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('3.3', '2', index)}
                            disabled={answerStatus['3.3-2'] === true && index !== correctAnswers['3.3-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['3.3-2'] === true && index !== correctAnswers['3.3-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['3.3-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(129, 199, 132, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          A wide base of support (feet shoulder-width apart) creates a stable foundation that helps prevent loss of balance during caregiving activities. This stability is crucial for both caregiver safety and patient security during transfers and mobility assistance.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which preventive measure is most important for caregivers to maintain their own physical health?
                    </Typography>
                    <List dense>
                      {['Perform regular stretching exercises', 'Avoid all lifting activities', 'Only work with lightweight patients'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['3.3-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['3.3-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['3.3-3'] && index === correctAnswers['3.3-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('3.3', '3', index)}
                            disabled={answerStatus['3.3-3'] === true && index !== correctAnswers['3.3-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['3.3-3'] === true && index !== correctAnswers['3.3-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['3.3-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(129, 199, 132, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Regular stretching exercises help maintain flexibility, prevent muscle stiffness, and reduce the risk of injury during caregiving activities. This preventive measure is crucial for caregivers' long-term physical health and ability to provide effective care.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    },
    {
      id: '3.4',
      title: '3.4 Effective Communication to Reduce Fear of Falling',
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <ChatIcon sx={{ mr: 1, color: '#81c784' }} /> 3.4 Effective Communication to Reduce Fear of Falling
          </Typography>
          
          <Typography paragraph>
            Communication plays a crucial role in addressing fear of falling and promoting confidence in mobility. 
            This section covers strategies for effective communication that supports fall prevention efforts.
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <FallRiskInfographics 
              type="communicationStrategies" 
              title="Communication Strategies for Fall Prevention"
              description="Effective approaches for discussing fall risks and prevention with care recipients to reduce fear and promote confidence."
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#81c784', fontWeight: 600 }}>
                    Verbal Communication Techniques
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><ChatIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Use clear, simple instructions" 
                        secondary="Avoid complex medical terminology and jargon"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ChatIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Focus on positive capabilities" 
                        secondary="Emphasize what the person can do safely, not limitations"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ChatIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Provide encouragement and reassurance" 
                        secondary="Acknowledge fears while building confidence"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ChatIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Explain procedures before starting" 
                        secondary="Reduce anxiety by eliminating uncertainty"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ChatIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Use a calm, confident tone" 
                        secondary="Your voice conveys security and competence"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#81c784', fontWeight: 600 }}>
                    Non-Verbal Communication Strategies
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><ChatIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Maintain appropriate eye contact" 
                        secondary="Creates connection and shows attentiveness"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ChatIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Use reassuring touch when appropriate" 
                        secondary="Gentle guidance can provide security during transfers"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ChatIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Demonstrate movements first" 
                        secondary="Visual examples improve understanding and confidence"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ChatIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Position yourself at eye level" 
                        secondary="Avoids intimidation and promotes equality"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><ChatIcon sx={{ color: '#81c784' }} /></ListItemIcon>
                      <ListItemText 
                        primary="Be aware of your body language" 
                        secondary="Rushed movements can increase anxiety"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quiz for Section 3.4 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#81c784', fontWeight: 600 }}>
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Which communication approach best addresses a care recipient's fear of falling?
                    </Typography>
                    <List dense>
                      {['Dismiss their concerns to avoid reinforcing fears', 'Validate concerns while providing reassurance', 'Limit their activities to prevent falls'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['3.4-1'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['3.4-1']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['3.4-1'] && index === correctAnswers['3.4-1']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('3.4', '1', index)}
                            disabled={answerStatus['3.4-1'] === true && index !== correctAnswers['3.4-1']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['3.4-1'] === true && index !== correctAnswers['3.4-1']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['3.4-1'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(129, 199, 132, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Validating concerns while providing reassurance acknowledges the person's feelings without dismissing them, while also offering support. This approach builds trust and allows for constructive discussion about strategies to address their specific fears.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What strategy helps build confidence in care recipients who fear falling?
                    </Typography>
                    <List dense>
                      {['Focusing on their limitations to increase caution', 'Encouraging gradual activity progression with achievable goals', 'Avoiding discussion of falls completely'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['3.4-2'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['3.4-2']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['3.4-2'] && index === correctAnswers['3.4-2']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('3.4', '2', index)}
                            disabled={answerStatus['3.4-2'] === true && index !== correctAnswers['3.4-2']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['3.4-2'] === true && index !== correctAnswers['3.4-2']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['3.4-2'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(129, 199, 132, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Encouraging gradual activity progression with achievable goals allows care recipients to experience success, which builds confidence. Starting with simpler tasks and gradually increasing difficulty creates a positive cycle of achievement that reduces fear of falling.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(129, 199, 132, 0.05)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is developing a fall response plan important for reducing fear of falling?
                    </Typography>
                    <List dense>
                      {['It eliminates the possibility of falls occurring', 'It focuses attention on the dangers of falling', 'It reduces anxiety by providing a clear course of action if a fall occurs'].map((option, index) => {
                        // Determine background color and icon color based on selection and correctness
                        let bgColor = 'transparent';
                        let iconColor = '#bdbdbd';
                        
                        if (selectedAnswers['3.4-3'] === index) {
                          // This is the selected answer
                          if (index === correctAnswers['3.4-3']) {
                            // Correct answer selected
                            bgColor = 'rgba(76, 175, 80, 0.2)';
                            iconColor = '#4caf50';
                          } else {
                            // Incorrect answer selected
                            bgColor = 'rgba(244, 67, 54, 0.2)';
                            iconColor = '#f44336';
                          }
                        } else if (showExplanations['3.4-3'] && index === correctAnswers['3.4-3']) {
                          // Highlight the correct answer when explanation is shown
                          bgColor = 'rgba(76, 175, 80, 0.1)';
                          iconColor = '#4caf50';
                        }
                        
                        return (
                          <ListItem 
                            key={index} 
                            button 
                            onClick={() => handleAnswerSelect('3.4', '3', index)}
                            disabled={answerStatus['3.4-3'] === true && index !== correctAnswers['3.4-3']}
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity: (answerStatus['3.4-3'] === true && index !== correctAnswers['3.4-3']) ? 0.6 : 1
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations['3.4-3'] && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(129, 199, 132, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Having a clear plan for what to do if a fall occurs reduces anxiety about the unknown. When care recipients know exactly what steps to take after a fall, they feel more in control, which can significantly reduce fear and improve confidence in daily activities.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )
    }
  ];
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button 
        variant="outlined" 
        startIcon={<ArrowBackIcon />} 
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Back to Learning Modules
      </Button>

      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ color: '#81c784', fontWeight: 600 }}>
          Module 3: Caregiver Best Practices and Mobility Assistance
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3, color: 'text.secondary' }}>
          This module covers safe techniques for transfers and ambulation, supporting independence while ensuring safety, 
          proper body mechanics for caregivers, and effective communication to reduce fear of falling.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <ModuleSidebar 
              sections={sections} 
              activeSection={activeSection} 
              onSectionChange={handleSectionChange} 
              moduleTitle="Caregiver Best Practices"
            />
          </Grid>
          <Grid item xs={12} md={9}>
            {sections.find(section => section.id === activeSection)?.content}
            
            {/* Module Buttons */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                variant="outlined" 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBack}
              >
                Back to Learning Modules
              </Button>
              {activeSection !== sections[sections.length - 1].id && (
                <Button 
                  variant="contained" 
                  endIcon={<ArrowForwardIcon />} 
                  onClick={handleNextSection}
                  sx={{ bgcolor: '#81c784', '&:hover': { bgcolor: '#4caf50' } }}
                >
                  Next Section
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default CaregiverBestPractices;
